import {
  useDataModel,
  useLazyDataModel,
  extend,
  params,
  arrayOf as arrayType,
  number as numberType,
  string as stringType,
  bool as boolType,
  customType
} from '@thd-nucleus/data-sources';
import { useStore } from '@thd-nucleus/experience-context';
import {
  useMemo, useState, useEffect, useRef, useCallback
} from 'react';
import { cleanQuantity } from '../AddToHelpers';

/**
 * Custom hook for calculating comprehensive list metrics including pricing
 * @param {Array} newItems - Array of items being added: [{itemId, quantity, price}, ...]
 * @param {Object|null} listData - List data object:
 *   - null: No data provided yet (wait)
 *   - {}: New list (no API call needed)
 *   - {id, storeId}: Existing list (need to fetch details)
 *   - {id, storeId, groups: [...]} : Existing list with complete data (no API call needed)
 * @returns {Object} - Object containing myListInformation and isLoading
 */
export const useListMetrics = (newItems = [], listData = null) => {
  const { storeId } = useStore();
  // Determine if we need to fetch list details based on listData state
  const listId = listData?.id;

  // Memoize list state flags to reduce dependencies
  const listState = useMemo(() => {
    const isWaitingForListData = listData === null;
    const isNewList = listData !== null && !listId;
    const hasCompleteListData = listData !== null && listData.groups !== undefined;
    const needsListDetailsFetch = listData !== null && listId && !hasCompleteListData;
    
    return {
      isWaitingForListData,
      isNewList,
      hasCompleteListData,
      needsListDetailsFetch
    };
  }, [listData, listId]);

  // Only fetch list details if we need to
  const shouldFetchListDetails = listState.needsListDetailsFetch;

  // Get list details only if we need to fetch them
  const {
    data: fetchedListDetails,
    loading: listDetailsLoading,
    error: listDetailsError,
  } = useDataModel('listProductDetails', {
    variables: {
      id: listId,
      storeId,
      sharedList: false, // Always false - users can't add to shared lists
    },
    skip: !shouldFetchListDetails,
    context: {
      withAuth: true
    }
  });

  // Use provided list data or fetched data
  // If listData has comprehensive data (like groups), use it directly
  // Otherwise use fetched data from the API
  const currentListDetails = (listData && listData.groups) ? listData : fetchedListDetails;

  // Price subtotal API for getting accurate pricing
  const [
    getPriceSubtotal,
    { data: priceData, loading: priceLoading, error: priceError }
  ] = useLazyDataModel('priceSubtotal', { context: { withAuth: true } });

  // State to track calculated list total
  const [calculatedListTotal, setCalculatedListTotal] = useState(0);

  // Ref to track last items hash to prevent duplicate API calls
  const lastItemsHashRef = useRef('');

  // Memoize stringified newItems to avoid unnecessary re-renders
  const newItemsString = useMemo(() => JSON.stringify(newItems), [newItems]);
  
  // Reset refs when newItems change (new add-to-list operation)
  useEffect(() => {
    lastItemsHashRef.current = '';
  }, [newItemsString]);

  /**
   * Helper function to add an item to the SKU quantities map
   * @param {Map} skuQuantities - Map to update
   * @param {Object} item - Item with itemId and quantity
   * @param {boolean} combineQuantities - Whether to combine with existing quantity
   */
  const addItemToQuantities = useCallback((skuQuantities, item, combineQuantities = false) => {
    if (!item?.itemId) return;
    
    const quantity = cleanQuantity(item.quantity);
    
    if (combineQuantities) {
      const existingQuantity = skuQuantities.get(item.itemId) || 0;
      skuQuantities.set(item.itemId, existingQuantity + quantity);
    } else {
      skuQuantities.set(item.itemId, quantity);
    }
  }, []);

  /**
   * Helper function to process groups and add items to SKU quantities
   * @param {Map} skuQuantities - Map to update
   * @param {Array} groups - Array of groups containing items
   */
  const processGroups = useCallback((skuQuantities, groups) => {
    if (!groups) return;
    
    groups.forEach((group) => {
      if (!group?.items) return;
      
      group.items.forEach((item) => {
        addItemToQuantities(skuQuantities, item, false);
      });
    });
  }, [addItemToQuantities]);

  /**
   * Calculate SKU quantities combining existing list items and new items
   * @returns {Map} - Map of itemId -> total quantity
   */
  const calculateSkuQuantities = useCallback(() => {
    const skuQuantities = new Map();
    const { isWaitingForListData, isNewList, hasCompleteListData, needsListDetailsFetch } = listState;

    // If waiting for list data, return empty map
    if (isWaitingForListData) {
      return skuQuantities;
    }

    // For new lists, only use added items
    if (isNewList) {
      newItems.forEach((newItem) => {
        addItemToQuantities(skuQuantities, newItem, true);
      });
      return skuQuantities;
    }

    // For existing lists with complete data, use listData.groups
    if (hasCompleteListData && listData?.groups) {
      processGroups(skuQuantities, listData.groups);
    } else if (needsListDetailsFetch && currentListDetails) {
      const actualListData = currentListDetails.listProductDetails || currentListDetails;
      
      if (!actualListData?.groups) return skuQuantities;
      
      processGroups(skuQuantities, actualListData.groups);
    }

    // Then, add new items (combining quantities if itemId already exists)
    newItems.forEach((newItem) => {
      addItemToQuantities(skuQuantities, newItem, true);
    });

    return skuQuantities;
  }, [
    addItemToQuantities,
    currentListDetails,
    listData,
    listState,
    newItemsString,
    processGroups
  ]);

  // Calculate list metrics automatically
  const myListInformation = useMemo(() => {
    // Use the shared calculation function
    const skuQuantities = calculateSkuQuantities();

    const uniqueSkuCount = skuQuantities.size;
    const quantityTotal = Array.from(skuQuantities.values()).reduce((sum, qty) => sum + qty, 0);

    const result = { 
      uniqueSkuCount, 
      quantityTotal, 
      listTotal: Math.round(calculatedListTotal * 100) / 100 
    };

    return result;
  }, [calculatedListTotal, calculateSkuQuantities]);

  // Helper function to call priceSubtotal API with change detection
  const callPriceSubtotal = useCallback((skuQuantities) => {
    // Create a hash of the current items to detect changes
    const itemsHash = JSON.stringify(Array.from(skuQuantities.keys()).sort().map((itemId) => ({
      itemId,
      quantity: skuQuantities.get(itemId)
    })));

    // Only call pricing API if items have actually changed
    if (itemsHash !== lastItemsHashRef.current) {
      lastItemsHashRef.current = itemsHash;

      const lineItems = Array.from(skuQuantities.keys()).map((itemId) => ({
        itemId,
        quantity: skuQuantities.get(itemId)
      }));

      getPriceSubtotal({
        variables: {
          lineItems,
          storeId
        }
      });
    }
  }, [getPriceSubtotal, storeId]);

  // Effect to trigger priceSubtotal API when data changes
  useEffect(() => {
    const { isWaitingForListData, isNewList, hasCompleteListData, needsListDetailsFetch } = listState;
    
    // If we're waiting for list data, do nothing
    if (isWaitingForListData) {
      return;
    }

    const skuQuantities = calculateSkuQuantities();

    // If it's a new list, we can calculate metrics directly
    if (isNewList && skuQuantities.size > 0) {
      callPriceSubtotal(skuQuantities);
      return;
    }

    // If we have complete list data, work with it
    if (hasCompleteListData && skuQuantities.size > 0) {
      callPriceSubtotal(skuQuantities);
      return;
    }

    // If we need to fetch list details first, wait for that to complete
    if (needsListDetailsFetch) {
      if (listDetailsLoading || listDetailsError) {
        return;
      }

      if (currentListDetails && skuQuantities.size > 0) {
        callPriceSubtotal(skuQuantities);
      }
    }
  }, [
    listState,
    listDetailsLoading,
    listDetailsError,
    currentListDetails,
    calculateSkuQuantities,
    callPriceSubtotal
  ]);

  // Effect to handle priceSubtotal API errors
  useEffect(() => {
    if (priceError) {
      // Fallback calculation if API fails using the shared function
      const skuQuantities = calculateSkuQuantities();

      // Calculate fallback total using provided prices
      const fallbackTotal = Array.from(skuQuantities.keys()).reduce((total, itemId) => {
        const quantity = skuQuantities.get(itemId);
        const price = newItems.find((newItem) => newItem.itemId === itemId)?.price || 0;
        return total + (price * quantity);
      }, 0);
      setCalculatedListTotal(fallbackTotal);
    }
  }, [priceError, calculateSkuQuantities, newItems]);

  // Handle priceSubtotal API response
  useEffect(() => {
    if (priceData && priceData.priceSubtotal && priceData.priceSubtotal.subtotal) {
      const apiTotal = priceData.priceSubtotal.subtotal;
      setCalculatedListTotal(apiTotal);
    }
  }, [priceData]);

  return {
    myListInformation,
    isLoading: listDetailsLoading || priceLoading,
    hasError: !!listDetailsError || !!priceError
  };
};

// Set up the data models
useListMetrics.dataModel = extend({
  listProductDetails: params({
    id: stringType().isRequired(),
    storeId: stringType().isRequired(),
    sharedList: boolType()
  }).shape({
    groups: arrayType(customType('Group').shape({
      items: arrayType(customType('Item').shape({
        itemId: stringType(),
        quantity: numberType(),
      }))
    }))
  }),
  priceSubtotal: params({
    lineItems: arrayType(customType('LineItem').shape({
      itemId: stringType(),
      quantity: numberType()
    })).isRequired(),
    storeId: stringType().isRequired()
  }).shape({
    subtotal: numberType()
  })
});
